// client/src/pages/marketing/ContactPage.jsx
import React, { useState, useRef } from 'react';
import { contactAPI } from '../../services/api';
import Navbar from '../../components/marketing/Navbar';
import Hero from '../../components/marketing/Hero';
import ContentBlock from '../../components/marketing/ContentBlock';
import Footer from '../../components/marketing/Footer';
import Turnstile from '../../components/common/Turnstile';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Naam is verplicht';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'E-mailadres is verplicht';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Voer een geldig e-mailadres in';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Bericht is verplicht';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Bericht moet minimaal 10 karakters bevatten';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!turnstileToken) {
      setErrors(prev => ({ ...prev, submit: 'Bevestig dat je geen robot bent' }));
      return;
    }

    setIsSubmitting(true);

    try {
      await contactAPI.submit({ ...formData, turnstileToken });

      setSubmitSuccess(true);

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setTurnstileToken('');
      turnstileRef.current?.reset();

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);

    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: 'Er is een fout opgetreden. Probeer het opnieuw.' });
      setTurnstileToken('');
      turnstileRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-100 text-charcoal-800 font-['Lexend_Deca'] text-base leading-relaxed">
      <Navbar />
      
      <Hero
        title="Neem Contact Op"
        subtitle="Vragen of opmerkingen? We helpen je graag verder"
        height="normal"
      />

      <main className="mx-auto max-w-3xl py-20 px-5">
        
        <ContentBlock title="Contact informatie">
          <p>
            Heb je vragen over onze trainingen, technische problemen, of wil je meer 
            informatie? Vul het onderstaande formulier in en we nemen zo snel mogelijk 
            contact met je op.
          </p>
          <div className="mt-6 space-y-3">
            <p className="flex items-start">
              <svg className="w-5 h-5 text-purple-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>
                <strong>E-mail:</strong> info@lerenmotiveren.nl<br />
              </span>
            </p>
            <p className="flex items-start">
              <svg className="w-5 h-5 text-purple-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                <strong>Reactietijd:</strong> We streven ernaar om binnen 2 werkdagen te reageren
              </span>
            </p>
          </div>
        </ContentBlock>

        {/* Contact Form */}
        <div className="my-12 bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
          <h2 className="font-['Lexend_Deca'] text-2xl text-gray-800 mb-6">
            Stuur ons een bericht
          </h2>

          {/* Success Message */}
          {submitSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="text-green-800 font-medium">Bedankt voor je bericht!</p>
                  <p className="text-green-700 text-sm mt-1">We nemen zo snel mogelijk contact met je op.</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Naam *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Je voor- en achternaam"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                E-mailadres *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="jouw@email.nl"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Subject Field */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Onderwerp (optioneel)
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Waar gaat je bericht over?"
              />
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Bericht *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  errors.message ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Typ hier je bericht..."
              />
              {errors.message && (
                <p className="mt-1 text-sm text-red-600">{errors.message}</p>
              )}
            </div>

            {/* Captcha */}
            <Turnstile
              ref={turnstileRef}
              onVerify={(token) => {
                setTurnstileToken(token);
                setErrors(prev => ({ ...prev, submit: '' }));
              }}
              onExpire={() => setTurnstileToken('')}
            />

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !turnstileToken}
              className={`w-full py-3 px-6 bg-purple-500 text-white font-semibold rounded-md transition-colors ${
                isSubmitting || !turnstileToken
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-purple-600'
              }`}
            >
              {isSubmitting ? 'Verzenden...' : 'Verstuur Bericht'}
            </button>

            <p className="text-sm text-gray-500 text-center">
              * Verplichte velden
            </p>
          </form>
        </div>

      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;